import { useMutation } from '@tanstack/react-query'
import { useCallback, useState } from 'react'

import { gameLuckService } from '../../../services/games'
import type { GameGoldMiner } from '../../../services/games/types'
import { useGlobalStore } from '../../../store/global-store'
import { notifyOpenRecharge } from '../../../commom/rn-bridge'

// A picareta anima pelo menos isto, mesmo que o servidor responda antes —
// sem isso o prêmio "pisca" e parece que nada aconteceu.
const MIN_MINING_MS = 1000
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

// Bloqueios do Jogo Responsável / saldo, em mensagem de gente
const FRIENDLY_PLAY_ERRORS: Record<string, string> = {
  consent_required:
    'Para jogar, assine o termo de Jogo Responsável no menu de jogos.',
  daily_limit_not_set:
    'Defina seu limite diário de perda no menu de jogos para jogar hoje.',
  daily_limit_reached:
    'Você atingiu seu limite de perda de hoje. Os jogos voltam amanhã!',
}

interface Params {
  gameGoldMinerData: GameGoldMiner[]
  timesBet: number
  isModalEarningVisible: boolean
  higherGameGoldMinerData: GameGoldMiner | null
  errorMessage: string | null
}

export const useCreateGoldMiner = () => {

  const [game, setGame] = useState<Params>({
    gameGoldMinerData: [],
    timesBet: 0,
    isModalEarningVisible: false,
    higherGameGoldMinerData: null,
    errorMessage: null,
  })

  const handleOpenModalRecharge = () => {
    notifyOpenRecharge()
  }

  // zurylive: o didder publicava o ganho alto no /LIVE-CHAT direto do navegador.
  // No zury o pubsub só aceita publish com Bearer PUBSUB_SECRET (401 pro cliente),
  // e o próprio worker de games já avisa o chat da live (liveId + chat_live_threshold
  // de apps_settings_games). Por isso a publicação do cliente saiu.

  const handleSuccess = useCallback(
    (data: GameGoldMiner[], timesBet: number) => {
      const notFoundCoins = data.some((result) => {
        return result.status === 'not_found_coins'
      })

      if (notFoundCoins) {
        return handleOpenModalRecharge()
      }

      setGame((prevState) => ({
        ...prevState,
        timesBet,
        gameGoldMinerData: data,
        // 10x com 10 presentes iguais vinha agrupado em 1 linha e o modal nem abria
        isModalEarningVisible: timesBet > 1 || data.length > 1,
      }))

      const checkHigher = data.find((gift) => gift.valueGift >= 520)
      if (checkHigher) {
        // if ('ReactNativeWebView' in window) {
        //   // @ts-ignore
        //   window.ReactNativeWebView.postMessage('earned-high-value-open')
        // }

        setGame((prevState) => ({
          ...prevState,
          higherGameGoldMinerData: checkHigher,
        }))
      }

      // usa o saldo autoritativo do backend (newCoins da ultima jogada) em vez de
      // refetch imediato — o debito no servidor e assincrono (fila SQS) e o refetch
      // retornaria o saldo anterior a jogada
      const lastNewCoins = data[data.length - 1]?.newCoins
      if (typeof lastNewCoins === 'number') {
        useGlobalStore.setState({ coinsInLive: lastNewCoins })
      }
    },
    [],
  )

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (params: { timesBet: number }) => {
      const [response] = await Promise.all([
        gameLuckService.createGoldMiner(params),
        sleep(MIN_MINING_MS),
      ])
      return response
    },
    onSuccess: (response, { timesBet }) => {
      // aposta recusada (termo, limite, saldo): antes `data` vinha null e a
      // tela quebrava calada — agora avisa e não mostra prêmio nenhum
      if (response?.error || !Array.isArray(response?.data)) {
        const code = String(response?.error ?? '')
        if (code === 'not_found_coins') return handleOpenModalRecharge()
        return setGame((prev) => ({
          ...prev,
          errorMessage:
            FRIENDLY_PLAY_ERRORS[code] ?? 'Falha no servidor. Tente novamente.',
        }))
      }
      handleSuccess(response.data, timesBet)
    },
    onError: () => {
      setGame((prev) => ({
        ...prev,
        errorMessage: 'Falha no servidor. Tente novamente.',
      }))
    },
  })

  // nunca rejeita: o erro já vira mensagem na tela (onError/onSuccess)
  const create = useCallback(
    (params: { timesBet: number }) => {
      setGame((prev) => ({ ...prev, errorMessage: null }))
      return mutateAsync(params).catch(() => undefined)
    },
    [mutateAsync],
  )

  return { ...game, isLoading: isPending, create, setGame }
}
